import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import SocialAudiencePanel from "@/pages/SocialAudiencePanel";
import Ads from "@/pages/Ads";
import LeadCapture from "@/pages/LeadCapture";
import ImageCropModal from "@/components/ImageCropModal";
import { loadRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import {
    LinkedinLogo, FacebookLogo, InstagramLogo, PaperPlaneTilt, Image as ImageIcon,
    VideoCamera, LinkSimple, CalendarBlank, ChartBar, Tray, ChatCircleDots, Users,
    PlusCircle, ArrowsClockwise, Plug, CheckCircle, XCircle, ThumbsUp, ShareNetwork,
    Clock, PencilSimple, Copy, Trash, Eye, CaretLeft, CaretRight, UserPlus, IdentificationCard,
    ArrowSquareOut, X, MagnifyingGlass, GearSix, FunnelSimple,
    Briefcase, WarningOctagon, Sparkle, Broadcast, LockKey,
} from "@phosphor-icons/react";

const PLATFORMS = [
    { key: "linkedin", label: "LinkedIn", Icon: LinkedinLogo, color: "#0A66C2", limit: 3000, video: true },
    { key: "facebook", label: "Facebook", Icon: FacebookLogo, color: "#1877F2", limit: 63206, video: true },
    { key: "instagram", label: "Instagram", Icon: InstagramLogo, color: "#E1306C", limit: 2200, video: true },
];
const P = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));

const TABS = [
    ["new-post", "New Post", PaperPlaneTilt],
    ["engagement", "Engagement", ChatCircleDots],
    ["library", "Post Library", Tray],
    ["performance", "Performance", ChartBar],
    ["inbox", "Inbox", Tray],
    ["calendar", "Calendar", CalendarBlank],
    ["setup", "Channels & Setup", Plug],
];

// Enterprise IA: 4 primary sections cleanly separating Organic vs Paid vs Lead Capture vs Setup.
const SECTIONS = [
    { key: "organic", label: "Organic", Icon: ShareNetwork,
      subs: [["organic-publish", "Publish"], ["engagement", "Engagement"], ["analytics", "Analytics"]] },
    { key: "paid", label: "Paid Ads", Icon: Broadcast, badge: "Pro", gate: "ads",
      subs: [["ads-campaigns", "Campaigns"], ["ads-leads", "Leads"], ["ads-performance", "Performance"]] },
    { key: "leadcapture", label: "Lead Capture", Icon: FunnelSimple, subs: [] },
    { key: "setup", label: "Setup", Icon: Plug,
      subs: [["setup-channels", "Channels"], ["setup-adaccount", "Ad Account"], ["setup-crm", "CRM & Lead Sync"]] },
];
const SECTION_OF = {
    "organic-publish": "organic", "new-post": "organic", "library": "organic", "calendar": "organic",
    "engagement": "organic", "inbox": "organic", "analytics": "organic", "performance": "organic",
    "ads-campaigns": "paid", "ads-leads": "paid", "ads-performance": "paid",
    "leadcapture": "leadcapture",
    "setup-channels": "setup", "setup": "setup", "setup-adaccount": "setup", "setup-crm": "setup",
};

const fmtDate = (s) => {
    if (!s) return "—";
    try { return new Date(s).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return s; }
};
const fmtDay = (s) => { try { return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return s; } };

const PlatformIcon = ({ k, size = 16 }) => {
    const p = P[k]; if (!p) return null;
    return <p.Icon size={size} weight="fill" style={{ color: p.color }} />;
};

const STATUS_BADGE = {
    draft: "bg-stone-100 text-stone-600", scheduled: "bg-amber-100 text-amber-700",
    published: "bg-emerald-100 text-emerald-700", failed: "bg-red-100 text-red-700",
};

const SENT_STYLE = {
    negative: { cls: "bg-red-100 text-red-700", label: "Negative" },
    neutral: { cls: "bg-stone-100 text-stone-600", label: "Neutral" },
    positive: { cls: "bg-emerald-100 text-emerald-700", label: "Positive" },
};
const SentimentBadge = ({ sentiment, urgency, size = "xs" }) => {
    if (!sentiment) return null;
    const s = SENT_STYLE[sentiment] || SENT_STYLE.neutral;
    const pad = size === "sm" ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[10px]";
    return (
        <span className={`inline-flex items-center gap-1 rounded-full font-bold ${pad} ${s.cls}`} data-testid={`sentiment-badge-${sentiment}`}>
            {sentiment === "negative" && urgency === "high" && <WarningOctagon size={size === "sm" ? 13 : 10} weight="fill" />}
            {s.label}{sentiment === "negative" && urgency === "high" ? " · Urgent" : ""}
        </span>
    );
};

function AdsLeadsView() {
    const [data, setData] = useState(null);
    const load = () => api.get("/ads/leads").then(({ data }) => setData(data)).catch(() => setData({ leads: [], total: 0, synced: 0 }));
    useEffect(() => { load(); }, []);
    const resync = async (l) => {
        try { await api.post(`/ads/leads/${l.id}/resync`); toast.success("Synced to Salesforce"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    };
    if (!data) return <div className="py-10 text-center text-sm text-stone2">Loading…</div>;
    return (
        <div data-testid="ads-leads-view">
            <div className="mb-4 flex flex-wrap gap-3">
                <div className="rounded-2xl border border-stone-200 bg-white px-5 py-3"><div className="text-2xl font-bold text-ink">{data.total}</div><div className="text-xs text-stone2">Total leads</div></div>
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3"><div className="text-2xl font-bold text-emerald-700">{data.synced}</div><div className="text-xs text-emerald-700/80">Synced to Salesforce</div></div>
            </div>
            {data.leads.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center text-sm text-stone2" data-testid="ads-leads-empty">No ad leads yet. Publish a campaign or send a test lead from Campaigns.</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-stone-200">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 text-left text-[11px] uppercase tracking-wide text-stone2">
                            <tr><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Contact</th><th className="px-4 py-2.5">Campaign</th><th className="px-4 py-2.5">Source</th><th className="px-4 py-2.5">Salesforce</th></tr>
                        </thead>
                        <tbody>
                            {data.leads.map((l) => (
                                <tr key={l.id} data-testid={`ads-lead-row-${l.id}`} className="border-t border-stone-100">
                                    <td className="px-4 py-2.5 font-medium">{l.fields?.full_name || "—"}</td>
                                    <td className="px-4 py-2.5 text-stone2">{l.fields?.email}<br />{l.fields?.phone}</td>
                                    <td className="px-4 py-2.5 text-stone2">{l.campaign_name}</td>
                                    <td className="px-4 py-2.5"><span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-bold uppercase">{l.source}</span></td>
                                    <td className="px-4 py-2.5">{l.synced
                                        ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle size={15} weight="fill" /> Synced</span>
                                        : <button onClick={() => resync(l)} className="inline-flex items-center gap-1 text-amber-600 hover:underline"><ArrowsClockwise size={14} /> Retry</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function AdsPerformance() {
    return (
        <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center" data-testid="ads-performance">
            <ChartBar size={40} weight="duotone" className="mx-auto text-stone-300" />
            <p className="mt-3 font-heading text-lg font-bold">Campaign performance</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-stone2">Spend, leads and cost-per-lead will appear here once you connect a live Meta ad account and a campaign starts delivering. (Meta returns no metrics for sandbox/unpublished ads.)</p>
        </div>
    );
}

function AdAccountSetup({ canAds, onUpgrade }) {
    const [status, setStatus] = useState(null);
    const [aid, setAid] = useState("");
    useEffect(() => { api.get("/ads/status").then(({ data }) => { setStatus(data); setAid(data.ad_account_id || ""); }).catch(() => setStatus({})); }, []);
    const save = async () => {
        try { await api.post("/ads/settings", { ad_account_id: aid.trim() }); toast.success("Ad account saved"); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    };
    if (!canAds) return <AdsPaywall onUpgrade={onUpgrade} />;
    return (
        <div className="max-w-xl space-y-4" data-testid="ads-account-setup">
            <div className={`flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm ${status?.facebook_connected ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                {status?.facebook_connected ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <XCircle size={18} weight="fill" className="text-amber-500" />}
                <div><div className="font-heading text-sm font-bold text-ink">Facebook Page</div><div className="text-xs text-stone2">{status?.facebook_connected ? (status.page_name || "Connected") : "Connect a Page under Setup → Channels first"}</div></div>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
                <label className="mb-1 block text-xs font-semibold text-stone2">Meta Ad Account ID</label>
                <div className="flex gap-2">
                    <input data-testid="ads-account-input" value={aid} onChange={(e) => setAid(e.target.value)} placeholder="act_1234567890 or 1234567890" className="w-full rounded-xl border border-stone-300 px-3 py-2 text-sm focus:border-ink focus:outline-none" />
                    <button data-testid="ads-account-save" onClick={save} className="rounded-xl bg-ink px-5 py-2 text-sm font-semibold text-white">Save</button>
                </div>
                <p className="mt-2 text-xs text-stone2">Found in Meta Ads Manager → Account overview. Campaigns publish into this ad account.</p>
            </div>
        </div>
    );
}

function CrmSyncSetup() {
    const [status, setStatus] = useState(null);
    useEffect(() => { api.get("/ads/status").then(({ data }) => setStatus(data)).catch(() => setStatus({})); }, []);
    const ok = status?.salesforce_connected;
    return (
        <div className="max-w-xl space-y-4" data-testid="crm-sync-setup">
            <div className={`flex items-center gap-2 rounded-2xl border px-4 py-3 text-sm ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
                {ok ? <CheckCircle size={18} weight="fill" className="text-emerald-600" /> : <XCircle size={18} weight="fill" className="text-amber-500" />}
                <div><div className="font-heading text-sm font-bold text-ink">Salesforce</div><div className="text-xs text-stone2">{ok ? "Connected — ad & landing-page leads auto-sync as Salesforce Leads" : "Not connected"}</div></div>
            </div>
            <div className="rounded-2xl border border-stone-200 bg-white p-5 text-sm text-stone2">
                <p>Every captured lead (ad forms and landing pages) is created as a Salesforce <b>Lead</b> with <b>LeadSource</b> set to its origin. Manage the CRM connection in the main <a href="/settings?tab=salesforce" className="font-semibold text-brand hover:underline">Settings → Salesforce</a> tab.</p>
            </div>
        </div>
    );
}

function AdsPaywall({ onUpgrade }) {
    return (
        <div className="mx-auto max-w-xl rounded-2xl border-2 border-brand/30 bg-gradient-to-br from-white via-amber-50/20 to-brand/5 p-8 text-center shadow-lg" data-testid="ads-paywall">
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-ink">
                <Broadcast size={22} weight="fill" className="text-brand" />
            </div>
            <h3 className="mt-5 font-heading text-2xl font-bold">Ad Campaign Manager</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-stone2">
                Run Meta lead-generation ad campaigns and auto-pipe every lead into Salesforce.
                It's included in the <b>Social Pro</b> plan.
            </p>
            <button data-testid="ads-paywall-upgrade" onClick={onUpgrade}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 font-heading text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-hover">
                <LockKey size={15} weight="fill" /> Upgrade to Social Pro
            </button>
        </div>
    );
}

export default function Social() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const rawTab = params.get("tab") || "organic-publish";
    // Normalize legacy tab values (old bookmarks / links) to the new section keys.
    const LEGACY = { ads: "ads-campaigns", leads: "leadcapture", setup: "setup-channels", "new-post": "organic-publish", audience: "analytics", performance: "analytics" };
    const tab = LEGACY[rawTab] || rawTab;
    const setTab = (t) => setParams((prev) => { const n = new URLSearchParams(prev); n.set("tab", t); return n; }, { replace: true });
    const canManage = user?.role === "admin" || user?.role === "supervisor";
    const canAds = !!user?.features?.ads;
    const [editingPost, setEditingPost] = useState(null);
    const [pubView, setPubView] = useState("compose");   // Organic → Publish inner toggle
    const [engView, setEngView] = useState("comments");  // Organic → Engagement inner toggle
    const [billing, setBilling] = useState(null);  // null = loading
    const loadBilling = () => api.get("/social-billing").then(({ data }) => setBilling(data)).catch(() => setBilling({ entitlement: { active: false, status: "locked", trial_available: false } }));
    useEffect(() => { loadBilling(); }, []);

    // OAuth return handling (?linkedin=connected / meta=connected|error|nopage)
    useEffect(() => {
        const map = { connected: ["success", "connected"], error: ["error", "connection failed"], nopage: ["error", "connected but no Facebook Page found"] };
        ["linkedin", "meta"].forEach((prov) => {
            const v = params.get(prov);
            if (v && map[v]) {
                const [kind, msg] = map[v];
                toast[kind](`${prov === "meta" ? "Facebook & Instagram" : "LinkedIn"} ${msg}`);
                setParams((prev) => { const n = new URLSearchParams(prev); n.delete(prov); return n; }, { replace: true });
            }
        });
    }, []); // eslint-disable-line

    const ent = billing?.entitlement;
    const locked = billing && !ent?.active;

    return (
        <AppShell>
        <div className="min-h-full bg-paper" data-testid="social-hub">
            <div className="border-b border-stone-200 bg-white px-6 pt-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand">Social · Add-on</div>
                        <h1 className="font-heading text-3xl font-bold text-ink">Social Hub</h1>
                        <p className="mt-1 text-sm text-stone2">Publish, engage and convert across LinkedIn, Facebook & Instagram — natively inside Inspiro Connect.</p>
                    </div>
                    {!locked && (
                        <button data-testid="social-setup-btn" onClick={() => setTab("setup")}
                            className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2 text-sm font-semibold text-ink shadow-sm transition-all hover:-translate-y-0.5 hover:border-brand/40">
                            <Plug size={16} weight="fill" className="text-brand" /> Channels & Setup
                        </button>
                    )}
                </div>
                {!locked && (() => {
                    const activeSection = SECTION_OF[tab] || "organic";
                    const cur = SECTIONS.find((s) => s.key === activeSection);
                    return (
                        <>
                            <nav className="mt-4 flex gap-1 overflow-x-auto no-scrollbar" data-testid="social-primary-tabs">
                                {SECTIONS.map((s) => {
                                    const active = activeSection === s.key;
                                    const dest = s.subs.length ? s.subs[0][0] : s.key;
                                    return (
                                        <button key={s.key} data-testid={`social-section-${s.key}`} onClick={() => setTab(dest)}
                                            className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${active ? "border-brand text-ink" : "border-transparent text-stone2 hover:border-stone-300 hover:text-ink"}`}>
                                            <s.Icon size={16} weight={active ? "fill" : "regular"} /> {s.label}
                                            {s.badge && <span className="rounded-full border border-amber-300/60 bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-800">{s.badge}</span>}
                                        </button>
                                    );
                                })}
                            </nav>
                            {cur?.subs?.length > 1 && (
                                <div className="mt-3 pb-1">
                                    <div className="inline-flex items-center gap-1 rounded-xl border border-stone-200/80 bg-stone-100 p-1" data-testid="social-subnav">
                                        {cur.subs.map(([val, label]) => (
                                            <button key={val} data-testid={`social-subtab-${val}`} onClick={() => setTab(val)}
                                                className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${tab === val ? "bg-white text-ink shadow-sm" : "text-stone-600 hover:text-ink"}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {ent?.status === "trialing" && (
                <div className="border-b border-amber-100 bg-amber-50 px-6 py-2 text-sm text-amber-800" data-testid="social-trial-banner">
                    You're on the Social free trial{ent.trial_ends_at ? ` — ends ${new Date(ent.trial_ends_at).toLocaleDateString()}` : ""}. <button onClick={() => setTab("setup")} className="font-bold underline">Subscribe</button> to keep access.
                </div>
            )}

            {billing === null ? (
                <div className="grid place-items-center p-20 text-sm text-stone-400" data-testid="social-loading">Loading…</div>
            ) : locked ? (
                <SocialPaywall billing={billing} canManage={user?.role === "admin"} onChanged={loadBilling} />
            ) : (
            <div className="p-6">
                {/* ORGANIC → Publish (Compose · Post Library · Calendar) */}
                {tab === "organic-publish" && (
                    <div>
                        <div className="mb-5 inline-flex items-center gap-1 rounded-xl border border-stone-200/80 bg-stone-100 p-1" data-testid="publish-inner-nav">
                            {[["compose", "Compose"], ["library", "Post Library"], ["calendar", "Calendar"]].map(([v, l]) => (
                                <button key={v} data-testid={`publish-view-${v}`} onClick={() => setPubView(v)}
                                    className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${pubView === v ? "bg-white text-ink shadow-sm" : "text-stone-600 hover:text-ink"}`}>{l}</button>
                            ))}
                        </div>
                        {pubView === "compose" && <NewPostTab canManage={canManage} initialPost={editingPost} onDone={() => { setEditingPost(null); setPubView("library"); }} />}
                        {pubView === "library" && <LibraryTab canManage={canManage} onCompose={() => { setEditingPost(null); setPubView("compose"); }} onEdit={(p) => { setEditingPost(p); setPubView("compose"); }} />}
                        {pubView === "calendar" && <CalendarTab onCompose={() => setPubView("compose")} />}
                    </div>
                )}

                {/* ORGANIC → Engagement (Comments · Inbox) */}
                {tab === "engagement" && (
                    <div>
                        <div className="mb-5 inline-flex items-center gap-1 rounded-xl border border-stone-200/80 bg-stone-100 p-1" data-testid="engagement-inner-nav">
                            {[["comments", "Comments"], ["inbox", "Inbox"]].map(([v, l]) => (
                                <button key={v} data-testid={`engagement-view-${v}`} onClick={() => setEngView(v)}
                                    className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${engView === v ? "bg-white text-ink shadow-sm" : "text-stone-600 hover:text-ink"}`}>{l}</button>
                            ))}
                        </div>
                        {engView === "comments" ? <EngagementTab /> : <InboxTab />}
                    </div>
                )}

                {/* ORGANIC → Analytics */}
                {tab === "analytics" && <PerformanceTab />}

                {/* PAID ADS → Campaigns · Leads · Performance */}
                {tab === "ads-campaigns" && (canAds ? <Ads embedded /> : <AdsPaywall onUpgrade={() => setTab("setup-adaccount")} />)}
                {tab === "ads-leads" && (canAds ? <AdsLeadsView /> : <AdsPaywall onUpgrade={() => setTab("setup-adaccount")} />)}
                {tab === "ads-performance" && (canAds ? <AdsPerformance /> : <AdsPaywall onUpgrade={() => setTab("setup-adaccount")} />)}

                {/* LEAD CAPTURE (Landing Pages · Forms · Submissions) */}
                {tab === "leadcapture" && <LeadCapture embedded />}

                {/* SETUP → Channels · Ad Account · CRM & Lead Sync */}
                {tab === "setup-channels" && <SetupTab canManage={user?.role === "admin"} billing={billing} onBillingChange={loadBilling} />}
                {tab === "setup-adaccount" && <AdAccountSetup canAds={canAds} onUpgrade={() => setTab("ads-campaigns")} />}
                {tab === "setup-crm" && <CrmSyncSetup />}
            </div>
            )}
        </div>
        </AppShell>
    );
}

function SocialPaywall({ billing, canManage, onChanged }) {
    const [cur, setCur] = useState(billing?.currency || "INR");
    const [cycle, setCycle] = useState("monthly");
    const [data, setData] = useState(billing);
    const [busy, setBusy] = useState("");
    const refetch = (c) => api.get("/social-billing", { params: { currency: c } }).then(({ data }) => setData(data));
    useEffect(() => { refetch(cur); }, [cur]); // eslint-disable-line
    const tiers = data?.tiers || [];
    const trialAvail = data?.entitlement?.trial_available;

    const startTrial = async () => {
        setBusy("trial");
        try { await api.post("/social-billing/trial"); toast.success("Free trial started — welcome to Social!"); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not start trial"); }
        finally { setBusy(""); }
    };
    const subscribe = async (tierId) => {
        setBusy(tierId);
        try {
            const ok = await loadRazorpay();
            if (!ok) { toast.error("Could not load payment window."); setBusy(""); return; }
            const { data: order } = await api.post("/social-billing/checkout", { tier: tierId, cycle, currency: cur });
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id, amount: order.amount, currency: order.currency,
                name: order.name, description: `Social ${tierId} (${cycle})`,
                order_id: order.order_id, prefill: order.prefill,
                handler: async (res) => {
                    try {
                        await api.post("/social-billing/verify", {
                            razorpay_order_id: res.razorpay_order_id,
                            razorpay_payment_id: res.razorpay_payment_id,
                            razorpay_signature: res.razorpay_signature });
                        toast.success("Social add-on activated 🎉"); onChanged?.();
                    } catch { toast.error("Payment captured but activation failed — contact support."); }
                    finally { setBusy(""); }
                },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(""); });
            rzp.open();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not start checkout"); setBusy(""); }
    };

    return (
        <div className="mx-auto max-w-5xl p-6" data-testid="social-paywall">
            <div className="rounded-2xl border border-stone-200 bg-white p-6 text-center shadow-sm">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand/10"><Plug size={22} weight="fill" className="text-brand" /></div>
                <h2 className="mt-3 font-heading text-2xl font-bold text-ink">Unlock the Social Hub</h2>
                <p className="mx-auto mt-1 max-w-xl text-sm text-stone2">Publish, engage and convert across LinkedIn, Facebook & Instagram. Social is a separate add-on{data?.entitlement?.status === "locked" && data?.promo_percent ? ` — ${data.promo_percent}% partner discount applied` : ""}.</p>
                {!canManage && <p className="mt-3 text-sm text-amber-700">Ask your workspace admin to start the trial or subscribe.</p>}
                <div className="mt-5 inline-flex items-center gap-2">
                    <div className="inline-flex overflow-hidden rounded-lg border border-stone-200 text-sm">
                        {["INR", "USD"].map((c) => <button key={c} data-testid={`pay-cur-${c}`} onClick={() => setCur(c)} className={`px-3 py-1.5 font-bold ${cur === c ? "bg-ink text-white" : "text-stone2"}`}>{c}</button>)}
                    </div>
                    <div className="inline-flex overflow-hidden rounded-lg border border-stone-200 text-sm">
                        {[["monthly", "Monthly"], ["yearly", "Yearly · save 2 mo"]].map(([c, l]) => <button key={c} data-testid={`pay-cycle-${c}`} onClick={() => setCycle(c)} className={`px-3 py-1.5 font-bold ${cycle === c ? "bg-ink text-white" : "text-stone2"}`}>{l}</button>)}
                    </div>
                </div>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-3">
                {tiers.map((t) => {
                    const price = cycle === "yearly" ? t.price_yearly : t.price_monthly;
                    return (
                        <div key={t.id} data-testid={`social-tier-${t.id}`} className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm ${t.id === "growth" ? "border-brand ring-1 ring-brand/30" : "border-stone-200"}`}>
                            {t.id === "growth" && <div className="mb-2 inline-block self-start rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand">Most popular</div>}
                            <div className="font-heading text-lg font-bold text-ink">{t.name}</div>
                            <div className="mt-1 text-sm text-stone2">{t.tagline}</div>
                            <div className="mt-3"><span className="text-3xl font-bold text-ink">{t.symbol}{price.toLocaleString()}</span><span className="text-sm text-stone-400">/{cycle === "yearly" ? "yr" : "mo"}</span></div>
                            {cur === "INR" && <div className="text-[11px] text-stone-400">+18% GST</div>}
                            <ul className="mt-4 flex-1 space-y-1.5 text-sm text-stone2">
                                {t.features.map((f, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle size={15} weight="fill" className="mt-0.5 shrink-0 text-emerald-500" /> {f}</li>)}
                            </ul>
                            <button data-testid={`social-subscribe-${t.id}`} disabled={!canManage || busy} onClick={() => subscribe(t.id)}
                                className={`mt-4 rounded-lg px-4 py-2.5 text-sm font-bold disabled:opacity-50 ${t.id === "growth" ? "bg-brand text-white hover:bg-brand-hover" : "bg-ink text-white hover:opacity-90"}`}>
                                {busy === t.id ? "Starting…" : "Subscribe"}
                            </button>
                        </div>
                    );
                })}
            </div>

            {trialAvail && canManage && (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-stone-300 bg-white p-5">
                    <div>
                        <div className="font-bold text-ink">Not ready to pay? Try it free.</div>
                        <div className="text-sm text-stone2">{data?.trial_days || 30}-day free trial · {data?.trial_seats || 1} user · no card required.</div>
                    </div>
                    <button data-testid="social-start-trial" disabled={busy} onClick={startTrial}
                        className="rounded-lg border border-brand px-4 py-2.5 text-sm font-bold text-brand hover:bg-brand/5 disabled:opacity-50">{busy === "trial" ? "Starting…" : "Start free trial"}</button>
                </div>
            )}
        </div>
    );
}

/* ---------------- SETUP / CONNECTIONS ---------------- */
const PROVIDER_OF = { linkedin: "linkedin", facebook: "meta", instagram: "meta" };

function SetupTab({ canManage }) {
    const [channels, setChannels] = useState([]);
    const [creds, setCreds] = useState(null);
    const [busy, setBusy] = useState(null);
    const [form, setForm] = useState({ linkedin_client_id: "", linkedin_client_secret: "", meta_app_id: "", meta_app_secret: "" });
    const [showCreds, setShowCreds] = useState(false);
    const origin = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
    const frontendOrigin = window.location.origin.replace(/\/+$/, "");

    const load = async () => {
        const [c1, c2] = await Promise.all([api.get("/social/channels"), api.get("/social/app-credentials")]);
        setChannels(c1.data.channels || []);
        setCreds(c2.data);
        setForm((f) => ({ ...f, linkedin_client_id: c2.data.linkedin.client_id || "", meta_app_id: c2.data.meta.app_id || "" }));
    };
    useEffect(() => { load(); }, []);

    const saveCreds = async () => {
        setBusy("creds");
        try {
            await api.put("/social/app-credentials", { redirect_base: origin, frontend_base: frontendOrigin, ...form });
            toast.success("Developer app credentials saved");
            setForm((f) => ({ ...f, linkedin_client_secret: "", meta_app_secret: "" }));
            await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not save"); }
        finally { setBusy(null); }
    };

    const connect = async (platform, action) => {
        const provider = PROVIDER_OF[platform];
        const configured = creds?.[provider]?.configured;
        setBusy(platform + action);
        try {
            if (action !== "disconnect" && configured) {
                const { data } = await api.get(`/social/oauth/${provider}/start`);
                window.location.href = data.authorize_url; // real OAuth redirect
                return;
            }
            await api.post(`/social/channels/${platform}/${action}`);
            toast.success(action === "disconnect" ? `${P[platform].label} disconnected` : `${P[platform].label} connected (demo)`);
            await load();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Action failed"); }
        finally { setBusy(null); }
    };

    return (
        <div className="mx-auto max-w-4xl" data-testid="social-setup">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="font-heading text-xl font-bold text-ink">Social Channel Connections</h2>
                    <p className="mt-1 text-sm text-stone2">Connect your business accounts via secure OAuth. Reconnect or disconnect anytime.</p>
                </div>
                {canManage && (
                    <button data-testid="social-creds-toggle" onClick={() => setShowCreds((v) => !v)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-ink hover:bg-stone-50">
                        <GearSix size={15} /> Developer App Setup
                    </button>
                )}
            </div>

            {canManage && showCreds && creds && (
                <div className="mt-4 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="social-creds-panel">
                    <h3 className="font-heading text-lg font-bold text-ink">Developer App Credentials</h3>
                    <p className="mt-1 text-sm text-stone2">Paste the keys from your own LinkedIn & Meta developer apps. Stored encrypted; you can change them anytime.</p>
                    <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                        <b>Register these exact Redirect / Callback URIs</b> in your developer apps:
                        <div className="mt-1 font-mono">LinkedIn: {origin}/api/social/oauth/linkedin/callback</div>
                        <div className="font-mono">Meta: {origin}/api/social/oauth/meta/callback</div>
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-sm font-bold text-ink"><LinkedinLogo size={16} weight="fill" style={{ color: "#0A66C2" }} /> LinkedIn {creds.linkedin.configured && <CheckCircle size={13} weight="fill" className="text-emerald-500" />}</div>
                            <Input label="Client ID" v={form.linkedin_client_id} on={(v) => setForm((f) => ({ ...f, linkedin_client_id: v }))} testid="creds-li-id" />
                            <Input label={creds.linkedin.configured ? "Client Secret (leave blank to keep)" : "Client Secret"} v={form.linkedin_client_secret} on={(v) => setForm((f) => ({ ...f, linkedin_client_secret: v }))} testid="creds-li-secret" />
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-sm font-bold text-ink"><FacebookLogo size={16} weight="fill" style={{ color: "#1877F2" }} /> Meta (Facebook + Instagram) {creds.meta.configured && <CheckCircle size={13} weight="fill" className="text-emerald-500" />}</div>
                            <Input label="App ID" v={form.meta_app_id} on={(v) => setForm((f) => ({ ...f, meta_app_id: v }))} testid="creds-meta-id" />
                            <Input label={creds.meta.configured ? "App Secret (leave blank to keep)" : "App Secret"} v={form.meta_app_secret} on={(v) => setForm((f) => ({ ...f, meta_app_secret: v }))} testid="creds-meta-secret" />
                        </div>
                    </div>
                    <button data-testid="social-creds-save" disabled={busy === "creds"} onClick={saveCreds}
                        className="mt-4 rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">Save Credentials</button>
                </div>
            )}

            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {channels.map((c) => {
                    const provider = PROVIDER_OF[c.platform];
                    const live = creds?.[provider]?.configured;
                    return (
                        <div key={c.platform} data-testid={`social-channel-${c.platform}`}
                            className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center justify-between">
                                <PlatformIcon k={c.platform} size={30} />
                                {c.connected
                                    ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700"><CheckCircle size={12} weight="fill" /> Connected</span>
                                    : <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-bold text-stone-500"><XCircle size={12} weight="fill" /> Not connected</span>}
                            </div>
                            <div className="mt-3 flex items-center gap-2 font-heading text-lg font-bold text-ink">{c.label}
                                {live ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-600">Live API</span>
                                      : <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-stone-400">Demo</span>}
                            </div>
                            <div className="mt-1 text-sm text-stone2">{c.connected ? c.account_name : "No account linked"}</div>
                            {c.connected && <div className="mt-1 text-xs text-stone-400">Last sync: {fmtDate(c.last_sync)}</div>}
                            {canManage ? (
                                <div className="mt-4 flex flex-wrap gap-2">
                                    {!c.connected ? (
                                        <button data-testid={`social-connect-${c.platform}`} disabled={busy} onClick={() => connect(c.platform, "connect")}
                                            className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-3 py-1.5 text-xs font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50">
                                            <Plug size={13} weight="fill" /> Connect
                                        </button>
                                    ) : (<>
                                        <button data-testid={`social-reconnect-${c.platform}`} disabled={busy} onClick={() => connect(c.platform, "reconnect")}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-bold text-ink hover:bg-stone-50 disabled:opacity-50">
                                            <ArrowsClockwise size={13} /> Reconnect
                                        </button>
                                        <button data-testid={`social-disconnect-${c.platform}`} disabled={busy} onClick={() => connect(c.platform, "disconnect")}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50">
                                            <XCircle size={13} /> Disconnect
                                        </button>
                                    </>)}
                                </div>
                            ) : <div className="mt-4 text-xs text-stone-400">Only workspace admins can manage connections.</div>}
                        </div>
                    );
                })}
            </div>
            {canManage && <SentimentGuardPanel />}
        </div>
    );
}

function SentimentGuardPanel() {
    const [cfg, setCfg] = useState(null);
    const [busy, setBusy] = useState(false);
    const [key, setKey] = useState("");
    const [test, setTest] = useState("");
    const [testResult, setTestResult] = useState(null);
    const load = () => api.get("/social/sentiment-config").then(({ data }) => setCfg(data));
    useEffect(() => { load(); }, []);
    if (!cfg) return null;
    const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

    const save = async () => {
        setBusy(true);
        try {
            const payload = { enabled: cfg.enabled, auto_case: cfg.auto_case, trigger: cfg.trigger,
                ai_enabled: cfg.ai_enabled, provider: cfg.provider, model: cfg.model };
            if (key.trim()) payload.api_key = key.trim();
            const { data } = await api.put("/social/sentiment-config", payload);
            setCfg(data); setKey("");
            toast.success("Sentiment Guard saved");
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not save"); }
        finally { setBusy(false); }
    };
    const runTest = async () => {
        if (!test.trim()) return;
        try { const { data } = await api.post("/social/sentiment-test", { text: test }); setTestResult(data); }
        catch { toast.error("Test failed"); }
    };

    return (
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="sentiment-guard-panel">
            <div className="flex items-center gap-2">
                <WarningOctagon size={18} weight="fill" className="text-red-500" />
                <h3 className="font-heading text-lg font-bold text-ink">Sentiment Guard</h3>
            </div>
            <p className="mt-1 text-sm text-stone2">Flag unhappy customers in Engagement and auto-open a Salesforce Case for negative or urgent comments. A free keyword engine runs by default — turn on AI (your own key) only if you want deeper accuracy.</p>

            <div className="mt-4 space-y-3">
                <label className="flex items-center justify-between rounded-xl border border-stone-200 px-4 py-3">
                    <span className="text-sm font-semibold text-ink">Analyze comment sentiment</span>
                    <input data-testid="sg-enabled" type="checkbox" checked={cfg.enabled} onChange={(e) => set({ enabled: e.target.checked })} className="h-4 w-4 accent-brand" />
                </label>

                <div className="rounded-xl border border-stone-200 px-4 py-3">
                    <label className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-ink">Auto-create Salesforce Case for flagged comments</span>
                        <input data-testid="sg-auto-case" type="checkbox" checked={cfg.auto_case} onChange={(e) => set({ auto_case: e.target.checked })} className="h-4 w-4 accent-brand" />
                    </label>
                    {cfg.salesforce_connected ? (
                        <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600" data-testid="sg-sf-connected">
                            <CheckCircle size={13} weight="fill" /> Salesforce is connected — Cases will be created automatically.
                        </div>
                    ) : (
                        <div className="mt-2 text-xs text-amber-700" data-testid="sg-sf-disconnected">
                            Salesforce isn't connected for this workspace. Connect it in{" "}
                            <a href="/settings" className="font-bold underline hover:text-amber-800">Settings → Integrations</a>{" "}
                            to enable auto-Case. Until then, negative comments are still flagged in Engagement (nothing is lost).
                        </div>
                    )}
                </div>

                <div className="rounded-xl border border-stone-200 px-4 py-3">
                    <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-stone2">Create a Case when a comment is…</div>
                    <select data-testid="sg-trigger" value={cfg.trigger} onChange={(e) => set({ trigger: e.target.value })}
                        className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none">
                        <option value="negative">Negative only</option>
                        <option value="negative_or_urgent">Negative OR high-urgency (threats, legal, refund, complaints)</option>
                    </select>
                </div>

                <div className="rounded-xl border border-stone-200 px-4 py-3">
                    <label className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"><Sparkle size={15} weight="fill" className="text-brand" /> Use AI (your own API key)</span>
                        <input data-testid="sg-ai-enabled" type="checkbox" checked={cfg.ai_enabled} onChange={(e) => set({ ai_enabled: e.target.checked })} className="h-4 w-4 accent-brand" />
                    </label>
                    {cfg.ai_enabled && (
                        <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <label className="text-sm">
                                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-stone2">Provider</span>
                                <select data-testid="sg-provider" value={cfg.provider} onChange={(e) => set({ provider: e.target.value })}
                                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none">
                                    <option value="openai">OpenAI</option><option value="gemini">Google Gemini</option><option value="anthropic">Anthropic</option>
                                </select>
                            </label>
                            <label className="text-sm">
                                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-stone2">Model</span>
                                <input data-testid="sg-model" value={cfg.model} onChange={(e) => set({ model: e.target.value })} placeholder="e.g. gpt-4o-mini"
                                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                            </label>
                            <label className="text-sm sm:col-span-2">
                                <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-stone2">{cfg.has_key ? "API key (leave blank to keep saved key)" : "API key (stored encrypted)"}</span>
                                <input data-testid="sg-api-key" type="password" autoComplete="new-password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={cfg.has_key ? "•••••••• saved" : "Paste your key"}
                                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                            </label>
                            <p className="text-[11px] text-stone-400 sm:col-span-2">Your key is used only for your workspace and billed to your provider account. It never leaves the server.</p>
                        </div>
                    )}
                </div>

                <div className="rounded-xl border border-dashed border-stone-200 px-4 py-3">
                    <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-stone2">Try it</div>
                    <div className="flex gap-2">
                        <input data-testid="sg-test-input" value={test} onChange={(e) => setTest(e.target.value)} placeholder="e.g. I don't like this, I want a refund"
                            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                        <button data-testid="sg-test-btn" onClick={runTest} className="shrink-0 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-ink hover:bg-stone-50">Preview</button>
                    </div>
                    {testResult && (
                        <div className="mt-2 flex items-center gap-2" data-testid="sg-test-result">
                            <SentimentBadge sentiment={testResult.sentiment} urgency={testResult.urgency} size="sm" />
                            <span className="text-xs text-stone2">{testResult.reason} · via {testResult.source === "ai" ? "AI" : "keyword engine"}</span>
                        </div>
                    )}
                </div>

                <div className="flex justify-end">
                    <button data-testid="sg-save" disabled={busy} onClick={save} className="rounded-lg bg-ink px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50">{busy ? "Saving…" : "Save Sentiment Guard"}</button>
                </div>
            </div>
        </div>
    );
}
const isVideoUrl = (u) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(u || "");
const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const absMediaUrl = (u) => {
    if (!u) return u;
    if (/^https?:\/\//i.test(u)) return u;
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
    return `${base}${u.startsWith("/") ? "" : "/"}${u}`;
};

function NewPostTab({ canManage, initialPost, onDone }) {
    const [channels, setChannels] = useState([]);
    const [selected, setSelected] = useState([]);
    const [content, setContent] = useState("");
    const [link, setLink] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [mediaType, setMediaType] = useState("image");  // image | video
    const [previewPlatform, setPreviewPlatform] = useState("linkedin");
    const [scheduleAt, setScheduleAt] = useState("");
    const [showSchedule, setShowSchedule] = useState(false);
    const [busy, setBusy] = useState("");
    const [uploading, setUploading] = useState(false);
    const [landingPages, setLandingPages] = useState([]);
    const [targeting, setTargeting] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [initialStatus, setInitialStatus] = useState(null);
    const [cropState, setCropState] = useState(null);  // {src, mime, filename} for the crop modal
    const fileRef = useRef(null);

    useEffect(() => { api.get("/social/channels").then(({ data }) => setChannels(data.channels || [])); }, []);
    useEffect(() => { api.get("/leadcapture/pages", { params: { status: "published" } }).then(({ data }) => setLandingPages(data.pages || [])).catch(() => {}); }, []);

    // Prefill when editing an existing post from the Library.
    useEffect(() => {
        if (!initialPost) { setEditingId(null); setInitialStatus(null); return; }
        setEditingId(initialPost.id);
        setInitialStatus(initialPost.status || "draft");
        setContent(initialPost.content || "");
        setLink(initialPost.link || "");
        setSelected(initialPost.platforms || []);
        setTargeting(initialPost.targeting || {});
        const att = initialPost.attachment || {};
        setImageUrl(att.url || "");
        setMediaType(att.type || (isVideoUrl(att.url) ? "video" : "image"));
        setScheduleAt(toLocalInput(initialPost.scheduled_at));
        setShowSchedule(!!initialPost.scheduled_at);
    }, [initialPost?.id]); // eslint-disable-line

    // Free the cropper's object URL on unmount (prevents a blob leak if the modal is open).
    useEffect(() => () => { if (cropState?.src) URL.revokeObjectURL(cropState.src); }, [cropState?.src]);

    const connectedKeys = channels.filter((c) => c.connected).map((c) => c.platform);
    const toggle = (k) => setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
    useEffect(() => { if (selected.length && !selected.includes(previewPlatform)) setPreviewPlatform(selected[0]); }, [selected]); // eslint-disable-line
    const activeLimit = P[previewPlatform]?.limit || 3000;

    const reset = () => { setContent(""); setLink(""); setImageUrl(""); setMediaType("image"); setSelected([]); setScheduleAt(""); setShowSchedule(false); setTargeting({}); setEditingId(null); setInitialStatus(null); };

    const doUpload = async (fileOrBlob) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", fileOrBlob);
            const { data } = await api.post("/social/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setImageUrl(absMediaUrl(data.url));
            setMediaType(data.type || "image");
            toast.success(`${data.type === "video" ? "Video" : "Image"} uploaded`);
        } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed"); }
        finally { setUploading(false); }
    };

    const onFilePick = async (e) => {
        const f = e.target.files?.[0];
        if (fileRef.current) fileRef.current.value = "";
        if (!f) return;
        // Images go through the crop/aspect-preset step first; videos upload directly.
        if (f.type.startsWith("image/")) {
            setCropState({ src: URL.createObjectURL(f), mime: f.type, filename: f.name });
        } else {
            await doUpload(f);
        }
    };

    const closeCrop = () => { if (cropState?.src) URL.revokeObjectURL(cropState.src); setCropState(null); };

    const submit = async (rawAction) => {
        // "save" (edit primary action) preserves the post's current state: a scheduled post stays
        // scheduled; a published post is updated in-place by the backend (never re-published).
        let action = rawAction;
        if (rawAction === "save") action = initialStatus === "scheduled" ? "schedule" : "draft";
        if (action !== "draft" && !selected.length) { toast.error("Select at least one platform."); return; }
        if (action === "schedule" && !scheduleAt) { setShowSchedule(true); toast.error("Pick a date & time."); return; }
        setBusy(rawAction);
        try {
            const url = imageUrl.trim();
            const type = url ? (mediaType === "video" || isVideoUrl(url) ? "video" : "image") : null;
            const payload = {
                content, link, platforms: selected, action,
                attachment: url ? { type, url } : null,
                scheduled_at: action === "schedule" ? new Date(scheduleAt).toISOString() : null,
                targeting,
            };
            if (editingId) await api.put(`/social/posts/${editingId}`, payload);
            else await api.post("/social/posts", payload);
            toast.success(editingId ? "Post updated" : action === "publish" ? "Published 🎉" : action === "schedule" ? "Scheduled" : "Draft saved");
            reset();
            if (editingId || action !== "draft") onDone?.();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not save"); }
        finally { setBusy(""); }
    };

    const isPublished = editingId && initialStatus === "published";

    if (!canManage) return <EmptyNote text="You need Social Manager access to compose posts. You can still manage interactions in Engagement & Inbox." />;

    return (
        <div className="grid gap-6 lg:grid-cols-[1.3fr_1fr]" data-testid="social-new-post">
            {cropState && (
                <ImageCropModal src={cropState.src} mime={cropState.mime} filename={cropState.filename}
                    onCancel={closeCrop}
                    onApply={async (file) => { closeCrop(); await doUpload(file); }} />
            )}
            <div className="rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-100 px-5 py-4">
                    <div>
                        <h2 className="font-heading text-lg font-bold text-ink">{editingId ? "Edit Post" : "Create New Post"}</h2>
                        <p className="text-sm text-stone2">{isPublished ? "This post is already live — edits are saved here; the platforms aren't re-posted." : editingId ? "Update this post and save your changes." : "Compose once, publish across platforms."}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button data-testid="social-post-cancel" onClick={reset}
                            className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-stone2 hover:bg-stone-50">Cancel</button>
                        <button data-testid="social-post-draft" disabled={busy} onClick={() => submit(editingId ? "save" : "draft")}
                            className="rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-ink hover:bg-stone-50 disabled:opacity-50">{editingId ? "Save Changes" : "Save Draft"}</button>
                        {!isPublished && (
                            <button data-testid="social-post-schedule" disabled={busy} onClick={() => showSchedule ? submit("schedule") : setShowSchedule(true)}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-white hover:bg-amber-600 disabled:opacity-50"><Clock size={15} weight="fill" /> {initialStatus === "scheduled" ? "Reschedule" : "Schedule"}</button>
                        )}
                        {!isPublished && (
                            <button data-testid="social-post-publish" disabled={busy} onClick={() => submit("publish")}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-3 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"><PaperPlaneTilt size={15} weight="fill" /> {editingId ? "Publish Now" : "Publish Now"}</button>
                        )}
                    </div>
                </div>
                <div className="space-y-5 p-5">
                    <div>
                        <div className="mb-2 text-xs font-bold uppercase tracking-wide text-stone2">Platforms</div>
                        <div className="grid grid-cols-3 gap-3">
                            {PLATFORMS.map((p) => {
                                const conn = connectedKeys.includes(p.key);
                                const on = selected.includes(p.key);
                                return (
                                    <button key={p.key} data-testid={`social-platform-${p.key}`} onClick={() => conn && toggle(p.key)} disabled={!conn}
                                        title={conn ? "" : "Connect in Channels & Setup"}
                                        className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-all ${on ? "border-brand bg-brand/5 text-ink" : "border-stone-200 text-stone2"} ${conn ? "hover:border-brand/40" : "cursor-not-allowed opacity-50"}`}>
                                        <p.Icon size={20} weight="fill" style={{ color: p.color }} /> {p.label}
                                    </button>
                                );
                            })}
                        </div>
                        {!connectedKeys.length && <p className="mt-2 text-xs text-amber-600">No channels connected yet — go to <b>Channels & Setup</b> to connect.</p>}
                    </div>
                    <div>
                        <div className="mb-1 flex items-center justify-between">
                            <span className="text-xs font-bold uppercase tracking-wide text-stone2">Post Content</span>
                            <span className={`text-xs ${content.length > activeLimit ? "text-red-500" : "text-stone-400"}`} data-testid="social-char-count">{content.length}{selected.length ? ` / ${activeLimit}` : ""}</span>
                        </div>
                        <textarea data-testid="social-post-content" value={content} onChange={(e) => setContent(e.target.value)} rows={6}
                            placeholder="What's on your mind?"
                            className="w-full rounded-xl border border-stone-200 px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15" />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="text-sm">
                            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-stone2"><ImageIcon size={13} /> Image / Video</span>
                            <div className="flex gap-2">
                                <input data-testid="social-post-image" value={imageUrl}
                                    onChange={(e) => { setImageUrl(e.target.value); setMediaType(isVideoUrl(e.target.value) ? "video" : "image"); }}
                                    placeholder="Paste a URL or upload →"
                                    className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                                <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" data-testid="social-upload-input" onChange={onFilePick} />
                                <button type="button" data-testid="social-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
                                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-sm font-semibold text-ink hover:bg-stone-50 disabled:opacity-50">
                                    {uploading ? <ArrowsClockwise size={15} className="animate-spin" /> : <ImageIcon size={15} weight="fill" className="text-brand" />} {uploading ? "Uploading…" : "Upload"}
                                </button>
                            </div>
                            {imageUrl && (
                                <div className="mt-1.5 flex items-center gap-2 text-[11px] text-stone-400">
                                    <span className="rounded-full bg-stone-100 px-2 py-0.5 font-bold capitalize text-stone2">{mediaType}</span>
                                    <button type="button" data-testid="social-clear-media" onClick={() => { setImageUrl(""); setMediaType("image"); }} className="font-bold text-red-500 hover:underline">Remove</button>
                                </div>
                            )}
                        </div>
                        <label className="text-sm">
                            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-stone2"><LinkSimple size={13} /> Link</span>
                            <input data-testid="social-post-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…"
                                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                        </label>
                    </div>
                    {landingPages.length > 0 && (
                        <label className="block text-sm" data-testid="social-add-landing-row">
                            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-stone2"><FunnelSimple size={13} /> Add Landing Page</span>
                            <select data-testid="social-add-landing" value=""
                                onChange={(e) => { const p = landingPages.find((x) => x.id === e.target.value); if (p) { setLink(`${window.location.origin}/p/${p.slug}`); toast.success(`Attached “${p.name}”`); } }}
                                className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none">
                                <option value="">Insert a published Lead Capture page URL…</option>
                                {landingPages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </label>
                    )}
                    {showSchedule && (
                        <label className="block text-sm" data-testid="social-schedule-row">
                            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-stone2"><CalendarBlank size={13} /> Schedule for</span>
                            <input data-testid="social-schedule-datetime" type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                                className="rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                        </label>
                    )}
                    <SocialAudiencePanel selected={selected} value={targeting} onChange={setTargeting} />
                </div>
            </div>

            {/* Preview */}
            <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="social-preview">
                <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wide text-stone2">Live Preview</span>
                    {selected.length > 1 && (
                        <div className="flex gap-1">
                            {selected.map((k) => (
                                <button key={k} onClick={() => setPreviewPlatform(k)} className={`rounded-md p-1.5 ${previewPlatform === k ? "bg-stone-100" : ""}`}>
                                    <PlatformIcon k={k} size={16} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <PostPreview platform={previewPlatform} account={channels.find((c) => c.platform === previewPlatform)?.account_name} content={content} imageUrl={imageUrl} mediaType={mediaType} link={link} />
            </div>
        </div>
    );
}

function PostPreview({ platform, account, content, imageUrl, mediaType, link }) {
    const p = P[platform] || P.linkedin;
    const isVideo = mediaType === "video" || isVideoUrl(imageUrl);
    return (
        <div className="rounded-xl border border-stone-200">
            <div className="flex items-center gap-2.5 p-3">
                <div className="grid h-9 w-9 place-items-center rounded-full" style={{ background: `${p.color}15` }}>
                    <p.Icon size={20} weight="fill" style={{ color: p.color }} />
                </div>
                <div className="leading-tight">
                    <div className="text-sm font-bold text-ink">{account || "Your Business"}</div>
                    <div className="text-[11px] text-stone-400">{p.label}</div>
                </div>
            </div>
            <div className="px-3 pb-2 text-sm text-ink whitespace-pre-wrap">{content || <span className="text-stone-300">Your post text will appear here…</span>}</div>
            {imageUrl ? (
                isVideo ? (
                    <video src={imageUrl} controls className="max-h-64 w-full bg-black object-contain" data-testid="social-preview-video" />
                ) : (
                    <img src={imageUrl} alt="" className="max-h-64 w-full object-cover" data-testid="social-preview-image" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )
            ) : (
                <div className="mx-3 mb-2 grid h-36 place-items-center rounded-lg bg-stone-50 text-xs text-stone-400">No attachment selected</div>
            )}
            {link && <div className="mx-3 mb-2 truncate rounded-lg border border-stone-200 px-3 py-2 text-xs text-brand">{link}</div>}
            <div className="flex items-center gap-4 border-t border-stone-100 px-3 py-2 text-xs text-stone-400">
                <span className="inline-flex items-center gap-1"><ThumbsUp size={13} /> Like</span>
                <span className="inline-flex items-center gap-1"><ChatCircleDots size={13} /> Comment</span>
                <span className="inline-flex items-center gap-1"><ShareNetwork size={13} /> Share</span>
            </div>
        </div>
    );
}

/* ---------------- ENGAGEMENT (3 column) ---------------- */
function EngagementTab() {
    const [posts, setPosts] = useState([]);
    const [platform, setPlatform] = useState("all");
    const [sort, setSort] = useState("recent");
    const [activePost, setActivePost] = useState(null);
    const [inters, setInters] = useState([]);
    const [interFilter, setInterFilter] = useState("all");
    const [sentFilter, setSentFilter] = useState("all");
    const [activeInter, setActiveInter] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const loadPosts = async () => {
        const { data } = await api.get("/social/posts", { params: { status: "published", platform } });
        let list = data.posts || [];
        if (sort === "oldest") list = [...list].reverse();
        if (sort === "engaged") list = [...list].sort((a, b) => (Object.values(b.engagement || {}).reduce((x, y) => x + y, 0)) - (Object.values(a.engagement || {}).reduce((x, y) => x + y, 0)));
        setPosts(list);
        if (list.length && !list.find((p) => p.id === activePost?.id)) setActivePost(list[0]);
    };
    useEffect(() => { loadPosts(); }, [platform, sort]); // eslint-disable-line
    const SENT_RANK = { negative: 0, neutral: 1, positive: 2 };
    const loadInters = () => {
        if (!activePost) { setInters([]); return; }
        api.get("/social/interactions", { params: { post_id: activePost.id, status: interFilter, sentiment: sentFilter } })
            .then(({ data }) => {
                // Negatives (unhappy customers) always float to the top so agents act first.
                const list = [...(data.interactions || [])].sort((a, b) => (SENT_RANK[a.sentiment] ?? 1) - (SENT_RANK[b.sentiment] ?? 1));
                setInters(list);
            });
    };
    const runSync = async (post) => {
        if (!post) return;
        setSyncStatus(null); setSyncing(true);
        try {
            const { data } = await api.post(`/social/posts/${post.id}/sync`);
            setSyncStatus(data.status || null);
        } catch { /* best-effort */ }
        finally { setSyncing(false); loadInters(); loadPosts(); }
    };
    useEffect(() => { setActiveInter(null); if (activePost) runSync(activePost); }, [activePost?.id]); // eslint-disable-line
    useEffect(() => { loadInters(); }, [interFilter, sentFilter]); // eslint-disable-line
    const restricted = syncStatus && Object.entries(syncStatus).find(([, v]) => v.message);

    return (
        <div className="grid gap-4 lg:grid-cols-[300px_minmax(0,1fr)_360px]" data-testid="social-engagement">
            {/* LEFT posts */}
            <div className="flex flex-col rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="border-b border-stone-100 p-3">
                    <div className="mb-2">
                        <Segmented size="sm" value={platform} onChange={setPlatform} options={channelIconOptions("All")} />
                    </div>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} className="w-full rounded-lg border border-stone-200 px-2 py-1.5 text-xs">
                        <option value="recent">Most Recent</option><option value="oldest">Oldest</option><option value="engaged">Most Engaged</option>
                    </select>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {posts.length === 0 && <EmptyNote small text="No published posts yet." />}
                    {posts.map((p) => (
                        <button key={p.id} data-testid={`social-eng-post-${p.id}`} onClick={() => setActivePost(p)}
                            className={`block w-full border-b border-stone-100 p-3 text-left transition-colors ${activePost?.id === p.id ? "bg-brand/5" : "hover:bg-stone-50"}`}>
                            <div className="flex items-center gap-2">
                                {(p.platforms || []).map((k) => <PlatformIcon key={k} k={k} size={14} />)}
                                <span className="ml-auto text-[10px] text-stone-400">{fmtDate(p.published_at)}</span>
                            </div>
                            <div className="mt-1 line-clamp-2 text-sm text-ink">{p.content}</div>
                            <div className="mt-1.5 flex gap-3 text-[11px] text-stone2">
                                <span className="inline-flex items-center gap-1"><ThumbsUp size={11} />{p.engagement?.likes || 0}</span>
                                <span className="inline-flex items-center gap-1"><ChatCircleDots size={11} />{p.engagement?.comments || 0}</span>
                                <span className="inline-flex items-center gap-1"><ShareNetwork size={11} />{p.engagement?.shares || 0}</span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* MIDDLE interactions */}
            <div className="flex flex-col rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="flex flex-col gap-2 border-b border-stone-100 p-3">
                    <div className="flex items-center gap-1">
                        <Segmented size="sm" value={interFilter} onChange={setInterFilter}
                            options={[["all", "All"], ["pending", "Pending"], ["replied", "Replied"]].map(([k, l]) => [k, l, null])} />
                        <button data-testid="social-eng-refresh" onClick={() => runSync(activePost)} disabled={syncing || !activePost}
                            className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold text-stone2 hover:bg-stone-100 disabled:opacity-40">
                            <ArrowsClockwise size={13} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Refresh"}
                        </button>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-stone2/70">Sentiment</span>
                        <Segmented size="sm" testidPrefix="social-eng-sentiment" value={sentFilter} onChange={setSentFilter}
                            options={[["all", "All"], ["negative", "Negative"], ["neutral", "Neutral"], ["positive", "Positive"]].map(([k, l]) => [k, l, null])} />
                    </div>
                </div>
                {restricted && (
                    <div className="border-b border-amber-100 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="social-eng-restricted">
                        {restricted[1].message || "Engagement can't be read from this platform's API."}{" "}
                        {restricted[1].permalink && <a href={restricted[1].permalink} target="_blank" rel="noreferrer" className="font-bold underline">View on {P[restricted[0]]?.label} →</a>}
                    </div>
                )}
                <div className="flex-1 overflow-y-auto">
                    {inters.length === 0 && <EmptyNote small text={restricted ? "Comments aren't available via this platform's API." : "No comments on this post yet."} />}
                    {inters.map((it) => (
                        <button key={it.id} data-testid={`social-inter-${it.id}`} onClick={() => setActiveInter(it)}
                            className={`flex w-full gap-3 border-b border-stone-100 p-3 text-left transition-colors ${it.sentiment === "negative" ? "border-l-4 border-l-red-500 bg-red-50/40" : ""} ${activeInter?.id === it.id ? "bg-brand/5" : "hover:bg-stone-50"}`}>
                            <Avatar name={it.profile?.name} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <span className="truncate text-sm font-semibold text-ink">{it.profile?.name}</span>
                                    <PlatformIcon k={it.platform} size={12} />
                                    <span className="ml-auto text-[10px] text-stone-400">{fmtDate(it.created_at)}</span>
                                </div>
                                <div className="mt-0.5 line-clamp-2 text-sm text-stone2">{it.text}</div>
                                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                    <StatusPill replied={it.reply_status === "replied"} />
                                    <SentimentBadge sentiment={it.sentiment} urgency={it.urgency} />
                                    {it.sf_case_id && <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700"><Briefcase size={10} weight="fill" /> Case</span>}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* RIGHT details */}
            <InteractionDetails interaction={activeInter} onChanged={() => { if (activePost) api.get("/social/interactions", { params: { post_id: activePost.id, status: interFilter, sentiment: sentFilter } }).then(({ data }) => setInters(data.interactions || [])); }} />
        </div>
    );
}

function InteractionDetails({ interaction, onChanged }) {
    const [reply, setReply] = useState("");
    const [crm, setCrm] = useState(null);
    const [sf, setSf] = useState({ salesforce_connected: false });
    const [casing, setCasing] = useState(false);
    useEffect(() => { api.get("/social/crm-status").then(({ data }) => setSf(data)).catch(() => {}); }, []);
    useEffect(() => { setReply(""); }, [interaction?.id]);
    if (!interaction) return <div className="grid place-items-center rounded-2xl border border-stone-200 bg-white text-sm text-stone-400 shadow-sm" data-testid="social-inter-details">Select an interaction</div>;

    const sendReply = async () => {
        if (!reply.trim()) return;
        try { await api.post(`/social/interactions/${interaction.id}/reply`, { text: reply }); toast.success("Reply sent"); setReply(""); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    };
    const createCase = async () => {
        setCasing(true);
        try {
            const { data } = await api.post(`/social/interactions/${interaction.id}/create-case`);
            toast.success(data.existing ? "Case already exists" : `Salesforce Case created${data.case_number ? ` (${data.case_number})` : ""}`);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not create Case"); }
        finally { setCasing(false); }
    };
    return (
        <div className="flex flex-col rounded-2xl border border-stone-200 bg-white shadow-sm" data-testid="social-inter-details">
            <div className="border-b border-stone-100 p-4">
                <div className="flex items-center gap-3">
                    <Avatar name={interaction.profile?.name} size={40} />
                    <div className="min-w-0">
                        <div className="font-semibold text-ink">{interaction.profile?.name}</div>
                        <div className="flex items-center gap-1.5 text-xs text-stone2"><PlatformIcon k={interaction.platform} size={12} /> {interaction.profile?.handle}</div>
                    </div>
                    <div className="ml-auto"><SentimentBadge sentiment={interaction.sentiment} urgency={interaction.urgency} size="sm" /></div>
                </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto p-4">
                <Field label="Original comment"><div className="rounded-lg bg-stone-50 p-3 text-sm text-ink">{interaction.text}</div></Field>
                {interaction.sentiment && (
                    <Field label="AI / Sentiment read">
                        <div className={`rounded-lg p-3 text-sm ${interaction.sentiment === "negative" ? "bg-red-50 text-red-800" : "bg-stone-50 text-stone2"}`} data-testid="social-inter-sentiment">
                            <span className="font-bold capitalize">{interaction.sentiment}</span>
                            {interaction.urgency && <span> · urgency {interaction.urgency}</span>}
                            {interaction.sentiment_reason && <div className="mt-0.5 text-xs opacity-80">{interaction.sentiment_reason}</div>}
                            {interaction.sentiment_source && <div className="mt-0.5 text-[10px] uppercase tracking-wide opacity-60">via {interaction.sentiment_source === "ai" ? "AI" : "keyword engine"}</div>}
                        </div>
                    </Field>
                )}
                {interaction.post && <Field label="Related post"><div className="line-clamp-2 text-sm text-stone2">{interaction.post.content}</div></Field>}
                <Field label="When"><span className="text-sm text-stone2">{fmtDate(interaction.created_at)}</span></Field>
                {interaction.replied_text && <Field label="Your reply"><div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">{interaction.replied_text}</div></Field>}
                <Field label="Reply">
                    <textarea data-testid="social-reply-box" value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Write a reply…"
                        className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" />
                    <button data-testid="social-reply-send" onClick={sendReply} className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-ink px-3 py-1.5 text-sm font-bold text-white hover:opacity-90"><PaperPlaneTilt size={14} weight="fill" /> Reply</button>
                </Field>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-stone-100 p-3">
                {interaction.sf_case_id ? (
                    sf.instance_url ? (
                        <a data-testid="social-view-case" href={`${sf.instance_url}/${interaction.sf_case_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-violet-100 px-3 py-1.5 text-xs font-bold text-violet-700 hover:bg-violet-200"><Briefcase size={13} weight="fill" /> View Case{interaction.sf_case_number ? ` ${interaction.sf_case_number}` : ""}</a>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-violet-100 px-3 py-1.5 text-xs font-bold text-violet-700"><Briefcase size={13} weight="fill" /> Case {interaction.sf_case_number || "created"}</span>
                    )
                ) : (
                    <button data-testid="social-create-case" disabled={casing || !sf.salesforce_connected} onClick={createCase}
                        title={sf.salesforce_connected ? "" : "Connect Salesforce first"}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50"><Briefcase size={13} weight="fill" /> {casing ? "Creating…" : "Create Case"}</button>
                )}
                {interaction.crm_type ? (
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-bold text-emerald-700"><CheckCircle size={13} weight="fill" /> {interaction.crm_type === "lead" ? "Lead" : "Contact"} created</span>
                ) : (<>
                    <button data-testid="social-create-lead" onClick={() => setCrm("lead")} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-hover"><UserPlus size={13} weight="fill" /> Create Lead</button>
                    <button data-testid="social-create-contact" onClick={() => setCrm("contact")} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-bold text-ink hover:bg-stone-50"><IdentificationCard size={13} /> Create Contact</button>
                </>)}
                {interaction.sf_id && sf.instance_url && (
                    <a data-testid="social-view-crm" href={`${sf.instance_url}/${interaction.sf_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-1.5 text-xs font-bold text-brand hover:bg-stone-50"><ArrowSquareOut size={13} /> View in Salesforce</a>
                )}
            </div>
            {crm && <CrmModal kind={crm} interaction={interaction} sfConnected={sf.salesforce_connected} onClose={() => setCrm(null)} onDone={() => { setCrm(null); onChanged?.(); }} />}
        </div>
    );
}

function CrmModal({ kind, interaction, sfConnected, onClose, onDone }) {
    const [form, setForm] = useState({ company: "", email: "", phone: "", notes: "", to_salesforce: sfConnected });
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/social/interactions/${interaction.id}/create-${kind}`, form);
            setResult(data);
            toast.success(`${kind === "lead" ? "Lead" : "Contact"} created${data.duplicate ? " (matched existing)" : ""}`);
        } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not create"); }
        finally { setBusy(false); }
    };
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="social-crm-modal">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-stone-100 px-5 py-3">
                    <h3 className="font-heading text-lg font-bold text-ink">Create {kind === "lead" ? "Lead" : "Contact"}</h3>
                    <button onClick={onClose} className="rounded-full p-1.5 text-stone2 hover:bg-stone-100"><X size={18} /></button>
                </div>
                {result ? (
                    <div className="space-y-4 p-6 text-center" data-testid="social-crm-result">
                        <CheckCircle size={44} weight="fill" className="mx-auto text-emerald-500" />
                        <div className="font-semibold text-ink">{kind === "lead" ? "Lead" : "Contact"} Created{result.duplicate ? " (existing match found)" : ""}</div>
                        {result.instance_url && result.sf_id && (
                            <a href={`${result.instance_url}/${result.sf_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><ArrowSquareOut size={15} /> View in Salesforce</a>
                        )}
                        <button onClick={onDone} className="block w-full rounded-lg border border-stone-200 py-2 text-sm font-semibold">Done</button>
                    </div>
                ) : (
                    <div className="space-y-3 p-5">
                        <div className="rounded-lg bg-stone-50 p-3 text-xs text-stone2">
                            <div><b className="text-ink">{interaction.profile?.name}</b> · {P[interaction.platform]?.label}</div>
                            <div className="mt-1 line-clamp-2">"{interaction.text}"</div>
                        </div>
                        <Input label="Company" v={form.company} on={(v) => set("company", v)} testid="crm-company" />
                        <Input label="Email" v={form.email} on={(v) => set("email", v)} testid="crm-email" />
                        <Input label="Phone" v={form.phone} on={(v) => set("phone", v)} testid="crm-phone" />
                        <label className="block text-sm"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-stone2">Notes</span>
                            <textarea data-testid="crm-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" /></label>
                        {sfConnected ? (
                            <label className="flex items-center gap-2 text-sm text-ink"><input data-testid="crm-to-sf" type="checkbox" checked={form.to_salesforce} onChange={(e) => set("to_salesforce", e.target.checked)} /> Also create in Salesforce</label>
                        ) : <p className="text-xs text-stone-400">Salesforce not connected — saved inside Inspiro Connect only.</p>}
                        <button data-testid="crm-submit" disabled={busy} onClick={submit} className="w-full rounded-lg bg-brand py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-50">
                            {form.to_salesforce ? "Create in Salesforce" : "Create"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ---------------- POST LIBRARY ---------------- */
function LibraryTab({ canManage, onCompose, onEdit }) {
    const [posts, setPosts] = useState([]);
    const [status, setStatus] = useState("all");
    const [platform, setPlatform] = useState("all");
    const [q, setQ] = useState("");
    const load = () => api.get("/social/posts", { params: { status, platform, q } }).then(({ data }) => setPosts(data.posts || []));
    useEffect(() => { load(); }, [status, platform, q]); // eslint-disable-line
    const del = async (id) => { if (!window.confirm("Delete this post?")) return; await api.delete(`/social/posts/${id}`); toast.success("Deleted"); load(); };
    const dup = async (id) => { await api.post(`/social/posts/${id}/duplicate`); toast.success("Duplicated to drafts"); load(); };

    return (
        <div data-testid="social-library">
            <div className="mb-4 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone2/70">Status</span>
                        <Segmented testidPrefix="social-lib-status" value={status} onChange={setStatus}
                            options={[["all", "All"], ["draft", "Draft"], ["scheduled", "Scheduled"], ["published", "Published"], ["failed", "Failed"]].map(([k, l]) => [k, l, null])} />
                    </div>
                    <div className="flex items-center gap-2.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone2/70">Channel</span>
                        <Segmented testidPrefix="social-lib-channel" value={platform} onChange={setPlatform}
                            options={channelSegOptions("All")} />
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <div className="relative">
                            <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-stone-400" />
                            <input data-testid="social-lib-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search posts…" className="w-44 rounded-lg border border-stone-200 py-1.5 pl-8 pr-3 text-sm focus:border-brand focus:outline-none" />
                        </div>
                        {canManage && <button data-testid="social-lib-compose" onClick={onCompose} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-bold text-white hover:bg-brand-hover"><PlusCircle size={15} weight="fill" /> New Post</button>}
                    </div>
                </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                {posts.length === 0 && <EmptyNote text="No posts match these filters." />}
                {posts.map((p) => (
                    <div key={p.id} data-testid={`social-lib-row-${p.id}`} className="flex flex-wrap items-center gap-3 border-b border-stone-100 p-4 last:border-0">
                        <div className="flex gap-1">{(p.platforms || []).map((k) => <PlatformIcon key={k} k={k} size={16} />)}</div>
                        {p.attachment?.url && (
                            (p.attachment.type === "video") ? (
                                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-stone-900 text-white" title="Video attachment"><VideoCamera size={16} weight="fill" /></div>
                            ) : (
                                <img src={p.attachment.url} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" data-testid={`social-lib-thumb-${p.id}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            )
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="line-clamp-1 text-sm font-medium text-ink">{p.content || "(no text)"}</div>
                            <div className="mt-0.5 text-xs text-stone-400">by {p.created_by_name} · {fmtDay(p.created_at)}{p.scheduled_at ? ` · scheduled ${fmtDate(p.scheduled_at)}` : p.published_at ? ` · published ${fmtDate(p.published_at)}` : ""}</div>
                        </div>
                        {p.status === "published" && <div className="hidden gap-3 text-xs text-stone2 sm:flex"><span className="inline-flex items-center gap-1"><ThumbsUp size={12} />{p.engagement?.likes || 0}</span><span className="inline-flex items-center gap-1"><ChatCircleDots size={12} />{p.engagement?.comments || 0}</span><span className="inline-flex items-center gap-1"><ShareNetwork size={12} />{p.engagement?.shares || 0}</span></div>}
                        <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold capitalize ${STATUS_BADGE[p.status]}`}>{p.status}</span>
                        {p.status === "published" && p.permalinks && Object.keys(p.permalinks).length > 0 && (
                            <div className="flex items-center gap-1" data-testid={`social-lib-viewpost-${p.id}`}>
                                {(p.platforms || []).filter((k) => p.permalinks[k]).map((k) => (
                                    <a key={k} href={p.permalinks[k]} target="_blank" rel="noreferrer" title={`View on ${P[k]?.label}`}
                                        data-testid={`social-lib-viewpost-${p.id}-${k}`}
                                        className="inline-flex items-center gap-1 rounded-lg border border-stone-200 px-2 py-1 text-[11px] font-bold text-stone2 hover:border-brand hover:text-brand">
                                        <PlatformIcon k={k} size={13} /> View <ArrowSquareOut size={11} weight="bold" />
                                    </a>
                                ))}
                            </div>
                        )}
                        {canManage && (
                            <div className="flex gap-1">
                                <button title="Edit" data-testid={`social-lib-edit-${p.id}`} onClick={() => onEdit?.(p)} className="rounded-md p-1.5 text-stone2 hover:bg-stone-100"><PencilSimple size={15} /></button>
                                <button title="Duplicate" onClick={() => dup(p.id)} className="rounded-md p-1.5 text-stone2 hover:bg-stone-100"><Copy size={15} /></button>
                                <button title="Delete" data-testid={`social-lib-delete-${p.id}`} onClick={() => del(p.id)} className="rounded-md p-1.5 text-red-500 hover:bg-red-50"><Trash size={15} /></button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------------- PERFORMANCE ---------------- */
function PerformanceTab() {
    const [platform, setPlatform] = useState("all");
    const [data, setData] = useState(null);
    useEffect(() => { api.get("/social/performance", { params: { platform } }).then(({ data }) => setData(data)); }, [platform]);
    if (!data) return <EmptyNote text="Loading…" />;
    const k = data.kpis;
    const cards = [["Total Posts", k.total_posts], ["Total Engagements", k.total_engagements], ["Comments", k.comments], ["Likes", k.likes], ["Shares", k.shares], ["Engagement Rate", k.engagement_rate]];
    const maxOt = Math.max(1, ...data.over_time.map((d) => d.engagements));
    const maxPl = Math.max(1, ...data.by_platform.map((d) => d.engagements));
    return (
        <div className="space-y-6" data-testid="social-performance">
            <div className="flex gap-1">
                {["all", "facebook", "instagram", "linkedin"].map((p) => (
                    <button key={p} onClick={() => setPlatform(p)} className={`rounded-lg px-3 py-1.5 text-xs font-bold ${platform === p ? "bg-ink text-white" : "border border-stone-200 text-stone2 hover:bg-stone-50"}`}>{p === "all" ? "All" : P[p].label}</button>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {cards.map(([label, val]) => (
                    <div key={label} data-testid={`social-kpi-${label.toLowerCase().replace(/ /g, "-")}`} className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
                        <div className="text-2xl font-bold text-ink">{val}</div>
                        <div className="mt-1 text-xs text-stone2">{label}</div>
                    </div>
                ))}
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
                <Card title="Engagement Over Time">
                    {data.over_time.length === 0 ? <EmptyNote small text="No data yet." /> : (
                        <div className="flex h-40 items-end gap-1.5">
                            {data.over_time.map((d) => (
                                <div key={d.date} className="flex flex-1 flex-col items-center gap-1" title={`${d.date}: ${d.engagements}`}>
                                    <div className="w-full rounded-t bg-brand" style={{ height: `${(d.engagements / maxOt) * 100}%` }} />
                                    <span className="text-[9px] text-stone-400">{d.date.slice(5)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
                <Card title="Engagement by Platform">
                    <div className="space-y-3">
                        {data.by_platform.map((d) => (
                            <div key={d.platform}>
                                <div className="mb-1 flex items-center justify-between text-xs"><span className="inline-flex items-center gap-1.5"><PlatformIcon k={d.platform} size={13} /> {d.label}</span><span className="text-stone2">{d.engagements} · {d.posts} posts</span></div>
                                <div className="h-2 rounded-full bg-stone-100"><div className="h-2 rounded-full" style={{ width: `${(d.engagements / maxPl) * 100}%`, background: P[d.platform].color }} /></div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
            <Card title="Top Performing Posts">
                {data.top_posts.length === 0 ? <EmptyNote small text="No posts yet." /> : (
                    <div className="divide-y divide-stone-100">
                        {data.top_posts.map((p) => (
                            <div key={p.id} className="flex items-center gap-3 py-2.5">
                                <div className="flex gap-1">{p.platforms.map((k) => <PlatformIcon key={k} k={k} size={14} />)}</div>
                                <div className="line-clamp-1 flex-1 text-sm text-ink">{p.content}</div>
                                <div className="flex gap-3 text-xs text-stone2"><span className="inline-flex items-center gap-1"><ThumbsUp size={12} />{p.engagement.likes || 0}</span><span className="inline-flex items-center gap-1"><ChatCircleDots size={12} />{p.engagement.comments || 0}</span><span className="inline-flex items-center gap-1"><ShareNetwork size={12} />{p.engagement.shares || 0}</span></div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
}

/* ---------------- INBOX ---------------- */
function InboxTab() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [channel, setChannel] = useState("all");
    const [agents, setAgents] = useState([]);
    const [active, setActive] = useState(null);
    const [sf, setSf] = useState({ salesforce_connected: false });
    const load = () => {
        const params = filter === "mine" ? { mine: true } : filter === "all" ? {} : { inbox: filter };
        if (channel !== "all") params.platform = channel;
        return api.get("/social/interactions", { params }).then(({ data }) => setItems(data.interactions || []));
    };
    useEffect(() => { load(); }, [filter, channel]); // eslint-disable-line
    useEffect(() => { api.get("/social/agents").then(({ data }) => setAgents(data.agents || [])); api.get("/social/crm-status").then(({ data }) => setSf(data)).catch(() => {}); }, []);
    const doAct = async (id, path, body) => { try { await api.post(`/social/interactions/${id}/${path}`, body || {}); load(); if (active?.id === id) setActive(null); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };

    const FILTERS = [["all", "All"], ["unread", "Unread"], ["needs_reply", "Needs Reply"], ["replied", "Replied"], ["mine", "Mine"], ["complete", "Complete"]];
    const unreadCount = items.filter((i) => !i.read).length;

    return (
        <div data-testid="social-inbox">
            {/* toolbar */}
            <div className="mb-4 rounded-2xl border border-stone-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                    <div className="flex items-center gap-2.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone2/70">Status</span>
                        <Segmented testidPrefix="social-inbox-filter" value={filter} onChange={setFilter}
                            options={FILTERS.map(([k, l]) => [k, k === "unread" && unreadCount ? `${l} (${unreadCount})` : l, null])} />
                    </div>
                    <div className="flex items-center gap-2.5">
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone2/70">Channel</span>
                        <Segmented testidPrefix="social-inbox-channel" value={channel} onChange={setChannel}
                            options={channelSegOptions("All")} />
                    </div>
                </div>
            </div>

            {/* list */}
            <div className="space-y-2.5">
                {items.length === 0 && <div className="rounded-2xl border border-stone-200 bg-white"><EmptyNote text="Nothing here right now." /></div>}
                {items.map((it) => {
                    const p = P[it.platform];
                    return (
                        <div key={it.id} data-testid={`social-inbox-row-${it.id}`}
                            onClick={() => setActive(it)}
                            className={`group flex cursor-pointer items-start gap-3 rounded-xl border bg-white p-4 shadow-sm transition-all hover:border-stone-300 hover:shadow-md ${!it.read ? "border-l-[3px] border-l-brand border-stone-200" : "border-stone-200"}`}>
                            {/* avatar + platform badge */}
                            <div className="relative shrink-0">
                                <Avatar name={it.profile?.name} size={42} />
                                <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full bg-white ring-1 ring-stone-200">
                                    {p && <p.Icon size={12} weight="fill" style={{ color: p.color }} />}
                                </span>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm font-bold text-ink">{it.profile?.name}</span>
                                    <span className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
                                        {p && <p.Icon size={10} weight="fill" style={{ color: p.color }} />} {p?.label}
                                    </span>
                                    {it.type && it.type !== "comment" && <span className="rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-stone-500">{it.type}</span>}
                                    <span className="ml-auto text-[10px] text-stone-400">{fmtDate(it.created_at)}</span>
                                </div>
                                <div className={`mt-1 line-clamp-2 text-sm ${!it.read ? "font-medium text-ink" : "text-stone2"}`}>{it.text}</div>
                                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                    {it.post && <span className="inline-flex max-w-[240px] items-center gap-1 truncate rounded-md bg-stone-50 px-2 py-0.5 text-[11px] text-stone-500"><ChatCircleDots size={11} /> {it.post.content || "post"}</span>}
                                    {it.assigned_to_name && <span className="inline-flex items-center gap-1 rounded-md bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-600"><Users size={10} weight="fill" /> {it.assigned_to_name}</span>}
                                    <StatusPill status={it.status} replied={it.reply_status === "replied"} />
                                </div>
                            </div>
                            {/* actions */}
                            <div className="flex shrink-0 flex-col items-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                                <button data-testid={`social-inbox-reply-${it.id}`} title="Open & reply" onClick={() => setActive(it)}
                                    className="inline-flex items-center gap-1 rounded-lg bg-ink px-2.5 py-1.5 text-[11px] font-bold text-white hover:opacity-90"><ChatCircleDots size={13} weight="fill" /> Reply</button>
                                <div className="flex items-center gap-1">
                                    <select data-testid={`social-inbox-assign-${it.id}`} value={it.assigned_to || ""} onChange={(e) => doAct(it.id, "assign", { user_id: e.target.value || null })} className="rounded-md border border-stone-200 bg-white px-1.5 py-1 text-[11px] text-ink focus:border-brand focus:outline-none">
                                        <option value="">Assign…</option>{agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                    <button title={it.read ? "Mark unread" : "Mark read"} onClick={() => doAct(it.id, "read", { read: !it.read })} className="rounded-md p-1.5 text-stone2 hover:bg-stone-100"><Eye size={14} /></button>
                                    <button title="Mark complete" data-testid={`social-inbox-complete-${it.id}`} onClick={() => doAct(it.id, "complete")} className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50"><CheckCircle size={14} /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {active && (
                <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={() => setActive(null)}>
                    <div className="h-full w-full max-w-md bg-paper shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="grid h-full grid-rows-[1fr]"><InteractionDetails interaction={active} onChanged={load} /></div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------- CALENDAR ---------------- */
function CalendarTab({ onCompose }) {
    const [cursor, setCursor] = useState(() => new Date());
    const [posts, setPosts] = useState([]);
    useEffect(() => { api.get("/social/posts").then(({ data }) => setPosts((data.posts || []).filter((p) => p.scheduled_at || p.published_at))); }, []);
    const y = cursor.getFullYear(), m = cursor.getMonth();
    const first = new Date(y, m, 1); const startDow = first.getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const cells = []; for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    const byDay = useMemo(() => {
        const map = {};
        posts.forEach((p) => { const s = p.scheduled_at || p.published_at; const dt = new Date(s); if (dt.getFullYear() === y && dt.getMonth() === m) { const k = dt.getDate(); (map[k] = map[k] || []).push(p); } });
        return map;
    }, [posts, y, m]);
    return (
        <div data-testid="social-calendar">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button onClick={() => setCursor(new Date(y, m - 1, 1))} className="rounded-lg border border-stone-200 p-1.5 hover:bg-stone-50"><CaretLeft size={16} /></button>
                    <h2 className="font-heading text-lg font-bold text-ink">{cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}</h2>
                    <button onClick={() => setCursor(new Date(y, m + 1, 1))} className="rounded-lg border border-stone-200 p-1.5 hover:bg-stone-50"><CaretRight size={16} /></button>
                </div>
                <button data-testid="social-cal-compose" onClick={onCompose} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-sm font-bold text-white hover:bg-brand-hover"><PlusCircle size={15} weight="fill" /> Create Post</button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="grid grid-cols-7 border-b border-stone-100 bg-stone-50 text-center text-[11px] font-bold uppercase text-stone2">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
                </div>
                <div className="grid grid-cols-7">
                    {cells.map((d, i) => (
                        <div key={i} className="min-h-[92px] border-b border-r border-stone-100 p-1.5">
                            {d && <div className="mb-1 text-xs font-semibold text-stone-400">{d}</div>}
                            <div className="space-y-1">
                                {(byDay[d] || []).map((p) => (
                                    <div key={p.id} title={p.content} className={`flex items-center gap-1 truncate rounded px-1.5 py-0.5 text-[10px] ${p.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                                        {(p.platforms || []).slice(0, 2).map((k) => <PlatformIcon key={k} k={k} size={10} />)}
                                        <span className="truncate">{p.content?.slice(0, 20) || "Post"}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------------- shared bits ---------------- */
const Avatar = ({ name, size = 34 }) => (
    <div className="grid shrink-0 place-items-center rounded-full bg-stone-800 font-semibold text-white ring-1 ring-stone-900/5"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}>{(name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>
);

/* Segmented control — the enterprise filter/tab pattern used across Social tabs */
const Segmented = ({ options, value, onChange, testidPrefix, size = "md" }) => (
    <div className="inline-flex flex-wrap items-center gap-0.5 rounded-xl bg-stone-100 p-1">
        {options.map(([k, label, icon]) => (
            <button key={k} type="button"
                data-testid={testidPrefix ? `${testidPrefix}-${k}` : undefined}
                onClick={() => onChange(k)}
                className={`inline-flex items-center gap-1.5 rounded-lg font-semibold transition-all ${size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"} ${value === k ? "bg-white text-ink shadow-sm ring-1 ring-stone-200/70" : "text-stone2 hover:text-ink"}`}>
                {icon}{label ? <span>{label}</span> : null}
            </button>
        ))}
    </div>
);

/* Platform channel options for the Segmented control (icon keeps its brand color) */
const channelSegOptions = (allLabel = "All Channels") => [
    ["all", allLabel, null],
    ...PLATFORMS.map((p) => [p.key, p.label, <p.Icon key={p.key} size={13} weight="fill" style={{ color: p.color }} />]),
];
const channelIconOptions = (allLabel = "All") => [
    ["all", allLabel, null],
    ...PLATFORMS.map((p) => [p.key, "", <p.Icon key={p.key} size={15} weight="fill" style={{ color: p.color }} />]),
];

/* Muted status indicator (dot + label) — replaces candy-colored pills */
const StatusPill = ({ status, replied }) => {
    const meta = status === "complete"
        ? { dot: "bg-emerald-500", label: "Complete" }
        : replied ? { dot: "bg-sky-500", label: "Replied" }
            : { dot: "bg-amber-500", label: "Needs reply" };
    return (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-stone-600 ring-1 ring-stone-200">
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} /> {meta.label}
        </span>
    );
};
const Field = ({ label, children }) => (<div><div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-stone2">{label}</div>{children}</div>);
const Input = ({ label, v, on, testid }) => (
    <label className="block text-sm"><span className="mb-1 block text-xs font-bold uppercase tracking-wide text-stone2">{label}</span>
        <input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:border-brand focus:outline-none" /></label>
);
const Card = ({ title, children }) => (<div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"><div className="mb-3 text-sm font-bold text-ink">{title}</div>{children}</div>);
const EmptyNote = ({ text, small }) => (<div className={`grid place-items-center text-center text-stone-400 ${small ? "p-6 text-xs" : "p-10 text-sm"}`}>{text}</div>);
